import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  HairlineCard,
  Icons,
  iconSize,
  palette,
  Stack,
  ToggleChip,
  Typography,
  useSnackbar,
} from '@munifocus/ui';
import {
  MAX_ASSESSED_PHOTOS,
  PHOTO_CONDITION_CODES,
  type PhotoAssessment,
  type PhotoConditionCode,
} from '@munifocus/contracts-gov';
import { useEvidence } from '../../lib/evidence-api';
import { useAssessPhotos, useConfirmPhotoAssessment } from '../../lib/ai-assistant-api';
import { apiErrorMessage } from '../../lib/mutation-toast';
import { currentLocale, isAssessablePhoto } from '../../lib/assistant-display';
import { AiLabel } from './AiLabel';

/** One photo's suggested conditions, which the officer toggles and confirms. */
function PhotoResult({
  fileId,
  photo,
  filename,
  thumbnail,
}: {
  fileId: string;
  photo: PhotoAssessment;
  filename: string;
  thumbnail: string | null;
}) {
  const { t } = useTranslation();
  const snackbar = useSnackbar();
  const confirm = useConfirmPhotoAssessment(fileId);
  const [chosen, setChosen] = useState<PhotoConditionCode[]>(photo.conditions);
  useEffect(() => setChosen(photo.conditions), [photo.conditions]);

  const toggle = (code: PhotoConditionCode) =>
    setChosen((list) => (list.includes(code) ? list.filter((c) => c !== code) : [...list, code]));

  return (
    <HairlineCard sx={{ p: 2 }}>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        {thumbnail && (
          <Box
            component="img"
            src={thumbnail}
            alt={filename}
            sx={{ width: 120, height: 90, objectFit: 'cover', borderRadius: 1, flexShrink: 0 }}
          />
        )}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="subtitle2" sx={{ wordBreak: 'break-word' }}>
            {filename}
          </Typography>
          {photo.skipped ? (
            <Typography variant="body2" sx={{ color: palette.text.secondary }}>
              {t(`gov.assistant.photos.skipped.${photo.skipped}`)}
            </Typography>
          ) : (
            <>
              <Box component="ul" sx={{ pl: 2.5, my: 1 }}>
                {photo.observations.map((o) => (
                  <Typography component="li" variant="body2" key={o}>
                    {o}
                  </Typography>
                ))}
              </Box>
              <Typography variant="caption" sx={{ color: palette.text.secondary }}>
                {t('gov.assistant.photos.conditionsHint')}
              </Typography>
              <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 1, mt: 1 }}>
                {PHOTO_CONDITION_CODES.map((code) => (
                  <ToggleChip
                    key={code}
                    label={t(`gov.assistant.photos.condition.${code}`)}
                    selected={chosen.includes(code)}
                    onToggle={() => toggle(code)}
                  />
                ))}
              </Stack>
              <Button
                sx={{ mt: 1.5 }}
                size="small"
                variant="outlined"
                startIcon={<Icons.Check size={iconSize.sm} aria-hidden />}
                disabled={confirm.isPending || confirm.isSuccess}
                onClick={() =>
                  confirm.mutate(
                    { evidenceId: photo.evidenceId, conditions: chosen },
                    {
                      onSuccess: () => snackbar.success(t('gov.assistant.photos.confirmed')),
                      onError: (err) =>
                        snackbar.error(
                          apiErrorMessage(err, t('gov.assistant.photos.confirmFailed')),
                        ),
                    },
                  )
                }
              >
                {confirm.isSuccess
                  ? t('gov.assistant.photos.confirmedShort')
                  : t('gov.assistant.photos.confirm')}
              </Button>
            </>
          )}
        </Box>
      </Stack>
    </HairlineCard>
  );
}

/**
 * Describe what is visible in evidence photos, to help the officer classify.
 * The officer picks up to four photos, reads the description, and confirms
 * the conditions they agree with — only that confirmation goes on the file.
 */
export function AssistantPhotos({ fileId }: { fileId: string }) {
  const { t, i18n } = useTranslation();
  const evidence = useEvidence(fileId);
  const assess = useAssessPhotos(fileId);
  const [selected, setSelected] = useState<string[]>([]);
  const photos = useMemo(
    () => (evidence.data ?? []).filter((e) => isAssessablePhoto(e.contentType)),
    [evidence.data],
  );
  const byId = useMemo(() => new Map((evidence.data ?? []).map((e) => [e.id, e])), [evidence.data]);

  if (evidence.isLoading) return <CircularProgress size={24} />;
  if (!photos.length) {
    return (
      <Typography variant="body2" sx={{ color: palette.text.secondary }}>
        {t('gov.assistant.photos.none')}
      </Typography>
    );
  }

  const toggle = (id: string) =>
    setSelected((list) =>
      list.includes(id)
        ? list.filter((x) => x !== id)
        : list.length < MAX_ASSESSED_PHOTOS
          ? [...list, id]
          : list,
    );

  return (
    <Stack spacing={2}>
      <Typography variant="body2" sx={{ color: palette.text.secondary }}>
        {t('gov.assistant.photos.pick', { count: MAX_ASSESSED_PHOTOS })}
      </Typography>
      <Stack spacing={0.5}>
        {photos.map((p) => (
          <FormControlLabel
            key={p.id}
            control={
              <Checkbox
                checked={selected.includes(p.id)}
                onChange={() => toggle(p.id)}
                disabled={!selected.includes(p.id) && selected.length >= MAX_ASSESSED_PHOTOS}
              />
            }
            label={p.note ? `${p.originalFilename} — ${p.note}` : p.originalFilename}
          />
        ))}
      </Stack>
      <Box>
        <Button
          variant="contained"
          startIcon={<Icons.Sparkles size={iconSize.md} aria-hidden />}
          disabled={!selected.length || assess.isPending}
          onClick={() =>
            assess.mutate({ evidenceIds: selected, locale: currentLocale(i18n.language) })
          }
        >
          {assess.isPending ? t('gov.assistant.working') : t('gov.assistant.photos.assess')}
        </Button>
      </Box>
      {assess.isError && (
        <Alert severity="error">
          {apiErrorMessage(assess.error, t('gov.assistant.photos.failed'))}
        </Alert>
      )}
      {assess.data && (
        <Box>
          <AiLabel provenance={assess.data} />
          <Stack spacing={1.5}>
            {assess.data.photos.map((photo) => {
              const row = byId.get(photo.evidenceId);
              return (
                <PhotoResult
                  key={photo.evidenceId}
                  fileId={fileId}
                  photo={photo}
                  filename={row?.originalFilename ?? photo.evidenceId}
                  thumbnail={row?.downloadUrl ?? null}
                />
              );
            })}
          </Stack>
        </Box>
      )}
    </Stack>
  );
}
